import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  input,
  Input,
  output,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {uniqId} from '@matchsource/utils';
import {MsTooltipDirective} from '../../directives/tooltip/tooltip.directive';
import {noop} from 'rxjs';

@Component({
  selector: 'ms-toggle-switch',
  templateUrl: './toggle-switch.component.html',
  styleUrls: ['./toggle-switch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleSwitchComponent),
      multi: true,
    },
  ],
  imports: [MsTooltipDirective],
})
export class ToggleSwitchComponent implements ControlValueAccessor {
  // Using mutable @Input properties here because signal inputs are read-only,
  // while ControlValueAccessor updates these values internally.
  @Input() disabled = false;
  @Input() checked = false;

  readonly tooltip = input<string>();

  readonly changeValue = output<boolean>();

  readonly inputId = uniqId('toggle-switch_');

  inputState: string;

  private readonly cdRef = inject(ChangeDetectorRef);

  private onValueChange: (value: boolean) => void = noop;
  private onTouch: () => void = noop;

  registerOnChange(fn: (value: boolean) => void): void {
    this.onValueChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdRef.markForCheck();
  }

  writeValue(obj: any): void {
    this.checked = !!obj;
    this.inputState = this.checked ? 'ms-toggle-switch-on' : 'ms-toggle-switch-off';
    this.cdRef.markForCheck();
  }

  blur(): void {
    this.onTouch();
  }

  click(): void {
    this.toggle();
    this.onValueChange(this.checked);
    this.changeValue.emit(this.checked);
  }

  private toggle(): void {
    this.checked = !this.checked;
  }
}
